import {
  AffectedProduct,
  AnomalyCase,
  CaseEvent,
  CaseStatus,
  KNOWN_ISSUES,
  PreValidationCheck,
  PreValidationVerdict,
} from '~/pages/CommercialOps/AriAnomalies/types';

import { request } from './common';

export type RollupDimension = 'channel' | 'chain' | 'vendorBrand' | 'territory';

/** Rollup rows carry the filter that reproduces them, so a drill-in is the same list query. */
const DIMENSIONS: Record<RollupDimension, { path: string; filterParam: string }> = {
  channel: { path: 'channel', filterParam: 'channel' },
  chain: { path: 'chain', filterParam: 'chain' },
  vendorBrand: { path: 'vendor-brand', filterParam: 'vendor_brand' },
  territory: { path: 'territory', filterParam: 'territory' },
};

export const ALL_STATUSES: Array<CaseStatus> = [
  'pending',
  'acknowledged',
  'resolved',
  'dismissed',
  'auto_resolved',
  'suppressed',
];

export interface CaseQuery {
  status: CaseStatus | 'all';
  tier: string;
  ruleKeys: Array<string>;
  dimension: { field: RollupDimension; value: string } | null;
  page: number;
  limit: number;
}

export interface CasePage {
  cases: Array<AnomalyCase>;
  total: number;
}

export interface CaseDetail {
  affectedProducts: Array<AffectedProduct>;
  history: Array<CaseEvent>;
  verdict: PreValidationVerdict;
  ranAt: string;
  checks: Array<PreValidationCheck>;
}

export interface CaseSummary {
  pending: number;
  confirmed: number;
  tier1: number;
}

export interface RollupRow {
  value: string;
  cases: number;
  confirmed: number;
  tier1: number;
  bookingsExposed: number;
}

export interface AriRule {
  ruleKey: string;
  label: string;
  description: string | null;
}

function basePath() {
  return `${window.configs.API_HOST}/api/reporting/ari`;
}

function present(value: string | null | undefined): string {
  return value ?? '—';
}

function dismissReason(row): string | undefined {
  const label = KNOWN_ISSUES.find((issue) => issue.key === row.dismissReasonKey)?.label ?? row.dismissReasonKey;
  if (!label) {
    return row.dismissNote ?? undefined;
  }
  return row.dismissNote ? `${label} — ${row.dismissNote}` : label;
}

function toCase(row): AnomalyCase {
  return {
    ...row,
    chain: present(row.chain),
    vendorBrand: present(row.vendorBrand),
    territory: present(row.territory),
    preValidation: { ...row.preValidation, checks: [] },
    affectedProducts: [],
    history: [],
    dismissReason: dismissReason(row),
  };
}

function caseParams(query: CaseQuery): URLSearchParams {
  const params = new URLSearchParams({
    status: query.status === 'all' ? ALL_STATUSES.join(',') : query.status,
    sort: 'priority',
    page: String(query.page),
    limit: String(query.limit),
  });

  if (query.tier !== 'all') {
    params.set('property_tier', query.tier);
  }
  if (query.ruleKeys.length) {
    params.set('rule_key', query.ruleKeys.join(','));
  }
  if (query.dimension) {
    params.set(DIMENSIONS[query.dimension.field].filterParam, query.dimension.value);
  }

  return params;
}

export async function listCases(query: CaseQuery, signal?: AbortSignal): Promise<CasePage> {
  const body = await request(`${basePath()}/cases?${caseParams(query)}`, { method: 'GET', signal });
  return { cases: body.cases.map(toCase), total: body.pagination.total };
}

type CheckEvidence = Record<string, string | number | boolean | null | Record<string, unknown>>;

/** Check evidence is stored as a JSON object per check, so it is flattened for display. */
function checkDetail(detail: CheckEvidence | null): string {
  if (!detail) {
    return '';
  }
  return Object.entries(detail)
    .map(([key, value]) => `${key.replace(/_/g, ' ')}: ${typeof value === 'object' ? JSON.stringify(value) : value}`)
    .join(' · ');
}

export async function getCaseDetail(caseId: string, signal?: AbortSignal): Promise<CaseDetail> {
  const [products, history, validation] = await Promise.all([
    request(`${basePath()}/cases/${caseId}/products`, { method: 'GET', signal }),
    request(`${basePath()}/cases/${caseId}/history`, { method: 'GET', signal }),
    request(`${basePath()}/cases/${caseId}/validation`, { method: 'GET', signal }),
  ]);

  return {
    affectedProducts: products.affectedProducts,
    history: history.history,
    verdict: validation.verdict,
    ranAt: validation.ranAt,
    checks: validation.checks.map((check) => ({
      label: check.summary ?? check.checkKey,
      supportsAlert: check.supportsAlert,
      detail: checkDetail(check.detail),
    })),
  };
}

function toRollupRow(row): RollupRow {
  return {
    value: present(row.value),
    cases: row.cases,
    confirmed: row.confirmed,
    tier1: row.tier_1,
    bookingsExposed: row.bookings_12m ?? 0,
  };
}

export async function getRollup(dimension: RollupDimension, signal?: AbortSignal): Promise<Array<RollupRow>> {
  const query = `status=${ALL_STATUSES.join(',')}`;
  const body = await request(`${basePath()}/cases/rollup/${DIMENSIONS[dimension].path}?${query}`, {
    method: 'GET',
    signal,
  });
  return body.rows.map(toRollupRow);
}

/**
 * The tiles count the whole pending queue, not the page on screen, so they come from an
 * aggregate rather than from the rows the list happens to be holding.
 */
export async function getCaseSummary(signal?: AbortSignal): Promise<CaseSummary> {
  const body = await request(`${basePath()}/cases/rollup/channel?status=pending`, { method: 'GET', signal });
  return body.rows.reduce(
    (totals: CaseSummary, row) => ({
      pending: totals.pending + row.cases,
      confirmed: totals.confirmed + row.confirmed,
      tier1: totals.tier1 + row.tier_1,
    }),
    { pending: 0, confirmed: 0, tier1: 0 },
  );
}

export interface TransitionBody {
  reason_key?: string;
  note?: string;
}

const TRANSITION_ACTIONS: Partial<Record<CaseStatus, string>> = {
  acknowledged: 'acknowledge',
  resolved: 'resolve',
  dismissed: 'dismiss',
};

export async function transitionCase(
  caseId: string,
  status: CaseStatus,
  body: TransitionBody = {},
): Promise<AnomalyCase> {
  const action = TRANSITION_ACTIONS[status];
  if (!action) {
    throw new Error(`No transition for status ${status}`);
  }

  const updated = await request(`${basePath()}/cases/${caseId}/${action}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  return toCase(updated);
}

export async function listKnownIssues(signal?: AbortSignal): Promise<Array<{ key: string; label: string }>> {
  const body = await request(`${basePath()}/known-issues`, { method: 'GET', signal });
  return body.knownIssues;
}

export async function listRules(signal?: AbortSignal): Promise<Array<AriRule>> {
  const body = await request(`${basePath()}/rules`, { method: 'GET', signal });
  return body.rules.map((rule) => ({
    ruleKey: rule.rule_key,
    label: rule.label,
    description: rule.description,
  }));
}
